import type { DisplayOrderTreeZone } from './DisplayOrderTreeZone';
import type { LinkPool } from './LinkPool';
import type { LinkId } from '../types';
import type { NodeId, StickyZoneId } from 'balus-common';
import { differenceBy } from 'lodash-es';

const findOtherParentZones = (
  childZoneId: StickyZoneId,
  currentZoneId: StickyZoneId,
  allZones: DisplayOrderTreeZone[]
): DisplayOrderTreeZone[] => {
  return allZones.filter((zone) => zone.id !== currentZoneId).filter((zone) => zone.hasZone(childZoneId));
};

const hasUnrenderedParents = (parentZones: DisplayOrderTreeZone[], renderedZoneIds: Set<StickyZoneId>): boolean => {
  return parentZones.some((zone) => !renderedZoneIds.has(zone.id));
};

const findChildZonesToExclude = (
  parentZone: DisplayOrderTreeZone,
  allZones: DisplayOrderTreeZone[],
  renderedZoneIds: Set<StickyZoneId>
): StickyZoneId[] => {
  return parentZone.zones
    .map((childZone) => childZone.id)
    .filter((childZoneId) => {
      const otherParentZones = findOtherParentZones(childZoneId, parentZone.id, allZones);
      return hasUnrenderedParents(otherParentZones, renderedZoneIds);
    });
};

// 順序ルール: https://docs.google.com/presentation/d/1pVI090GpRnhEA2ATDSj3UQNuz5oD5e0Afeb2CARN7G8/edit#slide=id.gb7ff438ae1_0_25
export const orderItemIds = (
  linkPool: LinkPool,
  zones: DisplayOrderTreeZone[],
  nodeIds: NodeId[],
  shouldExcludedZoneIds: StickyZoneId[],
  shouldExcludedNodeIds: NodeId[]
): (NodeId | StickyZoneId | LinkId)[] => {
  const zoneIds = zones.map((zone) => zone.id);

  // ゾーンのリンク
  let zoneLinks = linkPool.connectAndTakeConnectedLinks(zoneIds);

  const renderedZoneIds = new Set<StickyZoneId>();

  // ゾーン（内部の要素含む）とリンク
  const orderedZoneIds = zones.reduce<string[]>((acc, zone) => {
    // ゾーン同士を結ぶリンクは、どちらかのゾーンが描画される直前に描画されるようにする
    // そうすることで、リンクが他のゾーンの後ろに隠れてしまうのを防ぐ
    // https://github.com/levii/balus-app/issues/2676
    const connectedZoneLinks = zoneLinks.filter((zoneLink) => zoneLink.dest === zone.id || zoneLink.src === zone.id);
    const connectedLinkIds = connectedZoneLinks.map((link) => link.id);

    // 描画されるべきゾーンリンクを除去
    zoneLinks = differenceBy(zoneLinks, connectedZoneLinks, 'id');

    // 重複する親を持つ子ゾーンのうち、除外すべきものを取得
    // 複数の親を持つゾーンは、すべての親がレンダリング済みになるまで描画を先送りする
    const shouldExcludedChildZoneIds = findChildZonesToExclude(zone, zones, renderedZoneIds);

    // 現在のゾーンをレンダリング済みとして記録
    renderedZoneIds.add(zone.id);

    return [
      ...acc,
      ...connectedLinkIds,
      ...zone.order(linkPool, shouldExcludedNodeIds, [...shouldExcludedZoneIds, ...shouldExcludedChildZoneIds]),
    ];
  }, []);

  const nodeLinks = linkPool.connectAndTakeConnectedLinks(nodeIds);
  const nodeLinkIds = nodeLinks.map((rel) => rel.id);

  return orderedZoneIds.concat(nodeLinkIds).concat(nodeIds);
};
